import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import Logo from "@/components/Logo";
import { HIDDEN_PAYOUT_PATHS } from "@/lib/features";
import {
  LayoutDashboard, Network, Wallet, TrendingUp, ShoppingBag, FileCheck,
  Users, ShoppingCart, Package, BadgeIndianRupee, Store, LogOut, IdCard, Link2, Trophy, Megaphone, CreditCard, ShieldAlert,
  KeyRound, UserCircle, Gift, MessageSquare, FileText, FileSpreadsheet, ScrollText, BookOpen, Info,
  Menu, X,
} from "lucide-react";

const navByRole = {
  distributor: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/account-info", label: "Account Information", icon: Info },
    { to: "/welcome", label: "Welcome", icon: Megaphone },
    { to: "/id-card", label: "ID Card", icon: IdCard },
    { to: "/shop", label: "New Shopping", icon: ShoppingCart },
    { to: "/my-orders", label: "Order History", icon: Package },
    { to: "/tree", label: "Genealogy", icon: Network },
    { to: "/downline", label: "Direct Referral", icon: Users },
    { to: "/all-downline", label: "All IBA List", icon: Users },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/income", label: "Weekly Payout", icon: TrendingUp },
    { to: "/monthly-bonus", label: "Monthly Bonus", icon: Gift },
    { to: "/weekly-sales", label: "Weekly Sales", icon: TrendingUp },
    { to: "/rewards", label: "Rewards & Trips", icon: Trophy },
    { to: "/team-achievers", label: "Team Achievers", icon: Trophy },
    { to: "/payouts", label: "Payout History", icon: BadgeIndianRupee },
    { to: "/weekly-payouts", label: "Weekly Payout History", icon: FileSpreadsheet },
    { to: "/support", label: "Online Support", icon: MessageSquare },
    { to: "/referral", label: "Referral Link", icon: Link2 },
    { to: "/kyc", label: "KYC", icon: ShieldAlert },
    { to: "/bank", label: "Bank Details", icon: CreditCard },
    { to: "/profile", label: "Profile Photo", icon: UserCircle },
    { to: "/password-reset", label: "Password Reset", icon: KeyRound },
    { to: "/register", label: "Register a Member", icon: UserCircle },
  ],
  customer: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/my-orders", label: "My Orders", icon: ShoppingBag },
    { to: "/wallet", label: "Wallet", icon: Wallet },
    { to: "/kyc", label: "Upgrade to IBA", icon: FileCheck },
  ],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard },
    { to: "/account-info", label: "Account Information", icon: Info },
    { to: "/admin/members", label: "Members", icon: Users },
    { to: "/admin/bank-details", label: "Bank / Account Details", icon: CreditCard },
    { to: "/admin/sales-ledger", label: "Sales Ledger", icon: FileSpreadsheet },
    { to: "/admin/placement-moves", label: "Placement Moves", icon: Network },
    { to: "/admin/products", label: "Products", icon: Package },
    { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
    { to: "/admin/kyc", label: "KYC Approval", icon: IdCard },
    { to: "/admin/payouts", label: "Payouts", icon: BadgeIndianRupee },
    { to: "/admin/weekly-payouts", label: "Weekly Payout History", icon: FileSpreadsheet },
    { to: "/admin/payments", label: "Payments", icon: CreditCard },
    { to: "/admin/notices", label: "Notices", icon: Megaphone },
    { to: "/admin/grievances", label: "Grievances", icon: ShieldAlert },
    { to: "/admin/reports", label: "Reports", icon: FileSpreadsheet },
    { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
    { to: "/admin/glossary", label: "Glossary · Terms", icon: BookOpen },
    { to: "/admin/docs", label: "Documentation", icon: FileText },
    { to: "/team-achievers", label: "Team Achievers", icon: Trophy },
    { to: "/tree", label: "Tree View", icon: Network },
    { to: "/all-downline", label: "All IBA List", icon: Users },
    { to: "/register", label: "Register a Member", icon: UserCircle },
  ],
  // Superadmin gets the FULL admin nav plus the "Banners & Catalogue" page
  // (banner uploads with schedules + one-click canonical-catalogue rebuild)
  // pinned at the top. Members / Placement Moves / Audit Log are the three
  // pages where superadmin-only tools (force-activate, deactivate, override
  // filters) actually live — no separate menu entry required for those.
  superadmin: [
    { to: "/admin",              label: "Overview",           icon: LayoutDashboard },
    { to: "/account-info",       label: "Account Information", icon: Info },
    { to: "/superadmin",         label: "Banners & Catalogue",icon: Megaphone },
    { to: "/admin/members",      label: "Members",            icon: Users },
    { to: "/admin/bank-details", label: "Bank / Account Details", icon: CreditCard },
    { to: "/admin/sales-ledger", label: "Sales Ledger",       icon: FileSpreadsheet },
    { to: "/admin/placement-moves", label: "Placement Moves", icon: Network },
    { to: "/admin/products",     label: "Products",           icon: Package },
    { to: "/admin/orders",       label: "Orders",             icon: ShoppingCart },
    { to: "/admin/kyc",          label: "KYC Approval",       icon: IdCard },
    { to: "/admin/payouts",      label: "Payouts",            icon: BadgeIndianRupee },
    { to: "/admin/weekly-payouts", label: "Weekly Payout History", icon: FileSpreadsheet },
    { to: "/admin/payments",     label: "Payments",           icon: CreditCard },
    { to: "/admin/notices",      label: "Notices",            icon: Megaphone },
    { to: "/admin/grievances",   label: "Grievances",         icon: ShieldAlert },
    { to: "/admin/reports",      label: "Reports",            icon: FileSpreadsheet },
    { to: "/admin/audit-log",    label: "Audit Log",          icon: ScrollText },
    { to: "/admin/glossary",     label: "Glossary · Terms",   icon: BookOpen },
    { to: "/admin/docs",         label: "Documentation",      icon: FileText },
    { to: "/team-achievers",     label: "Team Achievers",     icon: Trophy },
    { to: "/tree",               label: "Tree View",          icon: Network },
    { to: "/all-downline",       label: "All IBA List",       icon: Users },
    { to: "/register",           label: "Register a Member",  icon: UserCircle },
  ],
  franchise: [
    { to: "/franchise", label: "Dashboard", icon: LayoutDashboard },
    { to: "/franchise/pos", label: "POS Billing", icon: Store },
    { to: "/my-orders", label: "Orders", icon: ShoppingBag },
    { to: "/wallet", label: "Commission Wallet", icon: Wallet },
    { to: "/tree", label: "Tree", icon: Network },
    { to: "/all-downline", label: "All IBA List", icon: Users },
    { to: "/referral", label: "Referral", icon: Link2 },
    { to: "/team-achievers", label: "Team Achievers", icon: Trophy },
    { to: "/register", label: "Register a Member", icon: UserCircle },
  ],
};

function roleLabel(role) {
  if (role === "distributor") return "IBA";
  if (role === "customer") return "Customer";
  if (role === "franchise") return "Franchise";
  if (role === "admin") return "Admin";
  if (role === "superadmin") return "Super Admin";
  return role;
}

export default function DashboardLayout({ children, title, subtitle, subtitleClassName, actions }) {
  const { user, logout } = useAuth();
  const { connected } = useRealtime();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer on route change
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  if (!user) return null;
  const items = (navByRole[user.role] || navByRole.distributor).filter((i) => !HIDDEN_PAYOUT_PATHS.includes(i.to));

  const SidebarInner = (
    <>
      <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
        <Logo light />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-close"
          className="md:hidden text-white/60 hover:text-white p-1"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>
      <div className="px-6 py-4 border-b border-white/10">
        <div className="text-xs uppercase tracking-widest text-gold">{user.user_code}</div>
        <div className="text-sm mt-1">{user.name}</div>
        <div className="text-xs text-white/50 mt-1">
          {roleLabel(user.role)} • {user.active ? "Active" : "Inactive"}
        </div>
      </div>
      <nav className="flex-1 py-4 overflow-y-auto">
        {items.map((it) => {
          const active = loc.pathname === it.to;
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={`sidebar-${it.to.replaceAll("/", "-")}`}
              className={`flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${
                active ? "bg-white/5 border-gold text-gold" : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon size={16} />
              {it.label}
            </Link>
          );
        })}
      </nav>
      <button
        onClick={() => { logout(); nav("/"); }}
        data-testid="sidebar-logout"
        className="m-4 px-4 py-2 text-xs text-white/60 hover:text-rose border border-white/10 rounded-sm flex items-center gap-2 justify-center"
      >
        <LogOut size={14} /> Logout
      </button>
    </>
  );

  return (
    <div className="min-h-screen flex bg-white">
      {/* Desktop sidebar (>= md): sticky column */}
      <aside className="w-64 bg-navy text-white hidden md:flex md:flex-col sticky top-0 h-screen">
        {SidebarInner}
      </aside>

      {/* Mobile drawer (< md): backdrop + off-canvas panel */}
      {mobileOpen && (
        <button
          type="button"
          data-testid="sidebar-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="md:hidden fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-150"
        />
      )}
      <aside
        data-testid="mobile-sidebar"
        className={`md:hidden fixed top-0 left-0 bottom-0 w-72 bg-navy text-white flex flex-col z-50 transition-transform duration-200 ease-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {SidebarInner}
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-border bg-white sticky top-0 z-10">
          <div className="px-4 md:px-6 lg:px-10 py-4 md:py-5 flex items-center gap-3">
            {/* Mobile hamburger — visible only < md */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-btn"
              className="md:hidden inline-flex items-center gap-1.5 -ml-1 px-2.5 py-1.5 rounded-full bg-navy text-white text-xs font-semibold shadow-sm active:scale-95 transition"
              aria-label="Open menu"
            >
              <Menu size={18} />
              <span>Menu</span>
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="font-serif text-xl md:text-2xl text-navy truncate">{title}</h1>
              {subtitle && <p data-testid="dashboard-subtitle" className={subtitleClassName || "text-xs md:text-sm text-navy/50 mt-0.5 truncate"}>{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-end">
              <span
                data-testid="realtime-indicator"
                title={connected ? "Live updates connected" : "Reconnecting…"}
                className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                  connected
                    ? "border-olive/30 bg-olive/5 text-olive"
                    : "border-rose/30 bg-rose/5 text-rose"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-olive animate-pulse" : "bg-rose"}`} />
                {connected ? "Live" : "Offline"}
              </span>
              {actions}
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 lg:p-10">{children}</div>
      </main>
    </div>
  );
}
